import { useState, useMemo, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardIcon, type CardSurface } from '@/components/ui/card'
import { ListCardHeader } from '@/components/ListCardHeader'
import { Button } from '@/components/ui/button'
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ChartLine,
  CircleAlert,
  BarChart3,
  Radio,
  Waves,
  Eye,
  ScanEye,
  MousePointerClick,
  Percent,
  Coins,
  Wallet,
  Receipt,
  Users,
  Gavel,
  Rows3,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { StatsApi, StatsQuery, StatsResponse, StatsTotals } from '@/services/stats/statsApi'
import {
  dateRangeLabel,
  getDateRange,
  isIntraday,
  type DateRange,
  type Flight,
} from '@/services/stats/statsRange'
import { useResolvedRange } from '../hooks/useResolvedRange'
import { DateRangeSelect } from './DateRangeSelect'
import { StatTile } from './StatTile'
import { StatTable } from './StatTable'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import { useStatsRange } from '../hooks/useStatsRange'
import { useStatsLayout, type StatDisplay } from '../hooks/useStatsLayout'
import { groupChangesByChunk, useChangeMarkers } from '../hooks/useChangeMarkers'
import { auditMessageText } from '../utils/auditMessage'
import { formatNumber, formatPercent, formatDate, formatTime } from '../utils/format'
import { AllStatsPopover } from './AllStatsPopover'
import type { AuditObjectType } from '@/services/shared/auditHistoryApi'
import { useStatLabel } from '../hooks/useStatLabel'
import {
  STAT_METRIC_CATALOG,
  formatStatValue,
  isCurrencyAmount,
  type StatMetricFormat,
  type StatMetricGroup,
} from './statsColumns'

/**
 * Advertising panels (line item, order, advertiser) count the traffic a line item could
 * have bid on — the API reports that as `bids`. Publishing panels count ad requests.
 */
type StatsVariant = 'publishing' | 'advertising'

const REQUESTS_FIELD: Record<StatsVariant, string> = {
  publishing: 'impressionRequests',
  advertising: 'bids',
}

function isFlatTimeSeries(stats: StatsResponse): boolean {
  const cr = stats.chunkRange
  return Array.isArray(cr) && cr.length > 0 && typeof cr[0] === 'string'
}

function formatFullNumber(n: number): string {
  return formatNumber(n)
}

function formatCurrency(amount: number, currency: string): string {
  return formatNumber(amount, { style: 'currency', currency, maximumFractionDigits: 2 })
}

function formatTimeLabel(iso: string): string {
  return formatTime(iso)
}

function formatDayLabel(iso: string): string {
  return formatDate(iso, { month: 'short', day: 'numeric' })
}

/** Chunk values are either plain numbers or `{ currency, amount }`. */
function chunkNumber(value: unknown): number {
  if (typeof value === 'number') return value
  if (value && typeof value === 'object' && 'amount' in value) {
    return (value as { amount: number }).amount ?? 0
  }
  return 0
}

/** Reads one field across the chunks, one value per slot in `chunkRange`. */
function chunkReader(stats: StatsResponse) {
  const range = (stats.chunkRange ?? []) as string[]
  const rawChunks = stats.chunks as Record<string, unknown>[]
  // The API omits empty buckets and does not guarantee chunk order, so match each
  // chunk to its slot in chunkRange by its `chunkStart` rather than by position.
  const byStart = new Map<number, Record<string, unknown>>()
  for (const chunk of rawChunks) {
    const start = chunk.chunkStart ?? chunk.start
    if (typeof start === 'string') byStart.set(new Date(start).getTime(), chunk)
  }
  return (key: string) =>
    range.map((iso) => chunkNumber(byStart.get(new Date(iso).getTime())?.[key]))
}

function extractChunkSeries(stats: StatsResponse, variant: StatsVariant) {
  const get = chunkReader(stats)
  const impressions = get('impressions')
  const clicks = get('clicks')
  return {
    requests: get(REQUESTS_FIELD[variant]),
    impressions,
    clicks,
    rendered: get('rendered'),
    viewables: get('viewables'),
    earnings: get('earnings'),
    cost: get('cost'),
    totalCost: get('totalCost'),
    uniqueUsers: get('uniqueUsers'),
    ctr: impressions.map((imp, i) => (imp > 0 ? (clicks[i] / imp) * 100 : 0)),
  }
}

function extractTotals(
  stats: StatsResponse,
  currency: string,
  viewabilityMethod: string,
  variant: StatsVariant
) {
  const t = stats.totals as StatsTotals
  if (!t) return null
  const requests = (t[REQUESTS_FIELD[variant]] as number | undefined) ?? 0
  const impressions = t.impressions ?? 0
  const clicks = t.clicks ?? 0
  const earnings = t.earnings?.amount ?? 0
  const cost = t.cost?.amount ?? 0
  const totalCost = t.totalCost?.amount ?? 0
  const uniqueUsers = t.uniqueUsers ?? 0
  const rendered = t.rendered ?? 0
  const viewable = t.viewables ?? 0
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0
  const viewabilityBase = viewabilityMethod === 'PER_IMPRESSION' ? impressions : rendered
  const viewability = viewabilityBase > 0 ? (viewable / viewabilityBase) * 100 : 0
  const renderedRate = impressions > 0 ? (rendered / impressions) * 100 : 0
  return {
    requests,
    impressions,
    clicks,
    earnings,
    cost,
    totalCost,
    uniqueUsers,
    ctr,
    viewability,
    rendered,
    viewable,
    renderedRate,
    currency,
  }
}

type ChunkSeries = ReturnType<typeof extractChunkSeries>
/** One bucket of the chart, each metric under the field the API reports it as. */
type ChartRow = { time: string; [field: string]: string | number }

function buildChartData(
  stats: StatsResponse,
  series: ChunkSeries,
  listed: Map<string, number[]>,
  range: DateRange,
  variant: StatsVariant
): ChartRow[] | null {
  if (!isFlatTimeSeries(stats)) return null
  const timeLabels = (stats.chunkRange ?? []) as string[]
  const formatLabel = isIntraday(range) ? formatTimeLabel : formatDayLabel
  return timeLabels.map((iso, i) => ({
    time: formatLabel(iso),
    slot: i,
    ...Object.fromEntries([...listed].map(([field, values]) => [field, values[i] ?? 0])),
    [REQUESTS_FIELD[variant]]: series.requests[i] ?? 0,
    impressions: series.impressions[i] ?? 0,
    rendered: series.rendered[i] ?? 0,
    viewables: series.viewables[i] ?? 0,
    clicks: series.clicks[i] ?? 0,
    ctr: series.ctr[i] ?? 0,
    earnings: series.earnings[i] ?? 0,
    cost: series.cost[i] ?? 0,
    totalCost: series.totalCost[i] ?? 0,
    uniqueUsers: series.uniqueUsers[i] ?? 0,
  }))
}

/**
 * Whether the last bucket was still collecting data when the stats were
 * fetched — today, or the current half hour — so its value is not final. The
 * bucket's length is read off the spacing of the last two.
 */
function isLastChunkOpen(stats: StatsResponse, fetchedAt: number): boolean {
  const range = (stats.chunkRange ?? []) as string[]
  if (range.length < 2) return false
  const last = new Date(range[range.length - 1]).getTime()
  const previous = new Date(range[range.length - 2]).getTime()
  return fetchedAt < last + (last - previous)
}


/**
 * Radius of a change marker's head. The chart's top margin has to clear it,
 * since the marker is centred on the top of the plot area.
 */
const CHANGE_FLAG_RADIUS = 7

/**
 * The head of a change marker: a dot at the top of the line, carrying the
 * number of changes when more than one landed in the same bucket. Drawn as SVG
 * because it lives inside the chart, so its colours come through `style`
 * rather than classes.
 */
function ChangeFlag({ viewBox, count }: { viewBox?: { x?: number; y?: number }; count: number }) {
  const x = viewBox?.x ?? 0
  const y = viewBox?.y ?? 0
  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={count > 1 ? CHANGE_FLAG_RADIUS : 4}
        style={{ fill: 'var(--ad-text-faint)' }}
      />
      {count > 1 && (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontWeight={700}
          style={{ fill: 'var(--ad-surface)' }}
        >
          {count}
        </text>
      )}
    </g>
  )
}

/* ── Metric definitions — drive both the tiles and the chart ─────────── */

type MetricKey =
  | 'requests'
  | 'impressions'
  | 'rendered'
  | 'viewables'
  | 'clicks'
  | 'ctr'
  | 'earnings'
  | 'cost'
  | 'totalCost'
  | 'uniqueUsers'

interface MetricDef {
  key: MetricKey
  labelKey: string
  color: string
  icon: LucideIcon
  /** `count` metrics share the left axis, `rate`/`currency` the right one. */
  scale: 'count' | 'rate' | 'currency'
}

/** Like {@link MetricDef}'s, with `decimal` for ratios such as bid responses per user. */
type MetricScale = MetricDef['scale'] | 'decimal'

/**
 * A line on the chart, keyed by the field the API reports it as — so a metric
 * is one choice whether it is picked from its tile or the all-stats list.
 */
interface ChartMetric {
  key: string
  labelKey: string
  color: string
  scale: MetricScale
}

/** The field the API reports a tile's metric as. */
function metricField(key: MetricKey, variant: StatsVariant): string {
  return key === 'requests' ? REQUESTS_FIELD[variant] : key
}

/** The axis a catalogued metric is drawn against, by the shape it comes in. */
const SCALE_BY_FORMAT: Record<StatMetricFormat, MetricScale> = {
  count: 'count',
  percent: 'rate',
  ratio: 'rate',
  currency: 'currency',
  decimal: 'decimal',
}

/**
 * The chart's palette. Lines from the all-stats list take the colours no tile
 * line is using, and the chart holds no more lines than there are colours.
 */
const CHART_COLORS = Array.from({ length: 9 }, (_, i) => `var(--ad-color-${i + 1})`)

/**
 * How the lines are scaled, as the old UI offered it: each against an axis of
 * its own, ticked in the line's colour so small numbers are not flattened by
 * big ones and units never mix — or all against one shared axis.
 */
type AxisMode = 'multiple' | 'shared'

const AXIS_MODE_LABELS: Record<AxisMode, string> = {
  multiple: 'stats.axesMultiple',
  shared: 'stats.axesShared',
}

const SHARED_AXIS = 'shared'

function getSavedAxisMode(key: string): AxisMode {
  try {
    return localStorage.getItem(key) === 'shared' ? 'shared' : 'multiple'
  } catch {
    return 'multiple'
  }
}

const METRICS: MetricDef[] = [
  {
    key: 'requests',
    labelKey: 'stats.requests',
    color: 'var(--ad-color-7)',
    icon: Radio,
    scale: 'count',
  },
  {
    key: 'impressions',
    labelKey: 'stats.impressions',
    color: 'var(--ad-color-1)',
    icon: Eye,
    scale: 'count',
  },
  // Only tiled for marketplace buyers, where impressions are what rendered.
  {
    key: 'rendered',
    labelKey: 'stats.rendered',
    color: 'var(--ad-color-7)',
    icon: Eye,
    scale: 'count',
  },
  {
    key: 'viewables',
    labelKey: 'stats.viewables',
    color: 'var(--ad-color-4)',
    icon: ScanEye,
    scale: 'count',
  },
  {
    key: 'clicks',
    labelKey: 'stats.clicks',
    color: 'var(--ad-color-2)',
    icon: MousePointerClick,
    scale: 'count',
  },
  { key: 'ctr', labelKey: 'stats.ctr', color: 'var(--ad-color-3)', icon: Percent, scale: 'rate' },
  {
    key: 'earnings',
    labelKey: 'stats.earnings',
    color: 'var(--ad-color-5)',
    icon: Coins,
    scale: 'currency',
  },
  {
    key: 'cost',
    labelKey: 'stats.cost',
    color: 'var(--ad-color-6)',
    icon: Wallet,
    scale: 'currency',
  },
  {
    key: 'totalCost',
    labelKey: 'stats.totalCost',
    color: 'var(--ad-color-9)',
    icon: Receipt,
    scale: 'currency',
  },
  {
    key: 'uniqueUsers',
    labelKey: 'stats.uniqueUsers',
    color: 'var(--ad-color-8)',
    icon: Users,
    scale: 'count',
  },
]

const DEFAULT_METRICS: MetricKey[] = ['impressions', 'viewables']

/**
 * Where a metric goes until the user moves it: the headline numbers get a
 * tile, the supporting ones a row in the table under the tiles. Everything
 * else in the all-stats list stays there until it is placed.
 */
const DEFAULT_DISPLAY: Record<MetricKey, StatDisplay> = {
  requests: 'table',
  impressions: 'card',
  rendered: 'table',
  viewables: 'card',
  clicks: 'card',
  ctr: 'table',
  earnings: 'card',
  cost: 'card',
  totalCost: 'table',
  uniqueUsers: 'table',
}

function getSavedMetrics(key: string, variant: StatsVariant): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? 'null')
    if (Array.isArray(saved)) {
      // Choices saved before metrics went by their field call the traffic tile
      // `requests`, which the API never reports under that name.
      const valid = saved
        .filter((k): k is string => typeof k === 'string')
        .map((k) => (k === 'requests' ? REQUESTS_FIELD[variant] : k))
      if (valid.length > 0) return valid
    }
  } catch {
    /* ignore malformed storage */
  }
  return DEFAULT_METRICS
}

/** Icons for metrics placed from the all-stats list, by the group each is listed under. */
const GROUP_ICONS: Record<StatMetricGroup, LucideIcon> = {
  traffic: Radio,
  delivery: Eye,
  engagement: MousePointerClick,
  money: Coins,
  rates: Percent,
}

/** A metric as a tile or a table row shows it. */
interface PlacedMetric {
  key: string
  label: string
  icon: LucideIcon
  color: string
  value: string
  sub?: string | null
  trend?: number[]
}

/* ── Main component ──────────────────────────────────────────────────── */

export interface StatsPanelProps {
  query: Partial<StatsQuery>
  storageKey?: string
  defaultRange?: DateRange
  /** Picks the wording and source field for the requests/available-traffic metric. */
  variant?: StatsVariant
  /**
   * Period to show. Pass this with `onRangeChange` to have the panel share its
   * period with other panels on the page; leave both out and it follows the
   * app-wide stats period.
   */
  range?: DateRange
  onRangeChange?: (range: DateRange) => void
  /**
   * Marks on the chart when this object was changed, so a step in delivery can
   * be read against what was done to it. Left out, nothing is marked.
   */
  auditObject?: { type: AuditObjectType; id: string }
  /** Dates of the object, offered in the period picker as its whole flight. */
  flight?: Flight
  /** Sunken by default, as stats are read-only figures; see {@link CardSurface}. */
  surface?: CardSurface
}

export const StatsPanel = memo(function StatsPanel({
  query: queryParams,
  storageKey = 'default',
  defaultRange = '24h',
  variant = 'publishing',
  range: controlledRange,
  onRangeChange,
  auditObject,
  flight,
  surface = 'sunken',
}: StatsPanelProps) {
  const { t } = useTranslation()
  const statLabel = useStatLabel()
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const eCpmMethod = currentNetwork?.eCpmMethod ?? 'PER_IMPRESSION'
  const viewabilityMethod = currentNetwork?.viewabilityMethod ?? 'PER_IMPRESSION'
  const hasToken = AuthService.isAuthenticated()
  const metricsKey = `adnuntius.stats.metrics.${storageKey}`
  const [ownRange, setOwnRange] = useStatsRange(defaultRange, flight)
  const range = controlledRange ?? ownRange
  const setRange = onRangeChange ?? setOwnRange
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(() =>
    getSavedMetrics(metricsKey, variant)
  )
  const axisModeKey = `adnuntius.stats.axes.${storageKey}`
  const [axisMode, setAxisMode] = useState<AxisMode>(() => getSavedAxisMode(axisModeKey))

  const changeAxisMode = (mode: AxisMode) => {
    setAxisMode(mode)
    localStorage.setItem(axisModeKey, mode)
  }

  /**
   * Each side gets the money it actually reads. A buyer has no earnings — what
   * a campaign cost, and what it cost including fees, is their side of the
   * impression — while a publisher has no use for the fees on someone else's
   * buy. Whichever tile is left out is still in the all-stats list.
   */
  const isMarketplaceBuyer = variant === 'advertising' && !!currentNetwork?.marketplacePlatform
  const metrics = useMemo(() => {
    const drop = variant === 'advertising' ? 'earnings' : 'totalCost'
    // A marketplace buyer leads with what they won — bid responses, then the
    // impressions that rendered of them. Available traffic stays in all stats.
    if (isMarketplaceBuyer) {
      return METRICS.filter((m) => m.key !== drop && m.key !== 'requests').map((m) =>
        m.key === 'impressions' ? { ...m, icon: Gavel } : m
      )
    }
    return METRICS.filter((m) => m.key !== drop && m.key !== 'rendered').map((m) =>
      // Advertising stats also name the traffic metric after what the buyer
      // bids on rather than after the requests behind it.
      variant === 'advertising' && m.key === 'requests'
        ? { ...m, labelKey: 'stats.availableTraffic', icon: Waves }
        : m
    )
  }, [variant, isMarketplaceBuyer])

  const defaultDisplay = useMemo(
    () =>
      Object.fromEntries(metrics.map((m) => [metricField(m.key, variant), DEFAULT_DISPLAY[m.key]])),
    [metrics, variant]
  )
  const layout = useStatsLayout(variant, defaultDisplay)

  // The picker keeps the choice the user made — which may be "the flight" —
  // while the queries below need the dates that works out to for this object.
  const effectiveRange = useResolvedRange(range, flight)

  const { startDate, endDate } = useMemo(
    () => getDateRange(effectiveRange, timeZone),
    [effectiveRange, timeZone]
  )
  const currency = queryParams.currency ?? currentNetwork?.defaultCurrency ?? 'NOK'

  const {
    data: stats,
    dataUpdatedAt,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['stats', queryParams, effectiveRange, startDate, endDate],
    queryFn: () => {
      const effectiveGroupBy =
        queryParams.groupBy === 'HALF_HOURLY' && !isIntraday(effectiveRange)
          ? 'DAILY'
          : queryParams.groupBy
      return StatsApi.get({
        startDate,
        endDate,
        currency,
        includeChunkRange: true,
        showFilteredBidReasons: true,
        timeout: 180000,
        ...queryParams,
        ...(effectiveGroupBy ? { groupBy: effectiveGroupBy } : {}),
      })
    },
    enabled: hasToken && !!currentNetwork,
  })

  const isFlat = useMemo(() => (stats ? isFlatTimeSeries(stats) : false), [stats])

  const totals = useMemo(() => {
    if (!stats || !isFlat) return null
    return extractTotals(stats, currency, viewabilityMethod, variant)
  }, [stats, isFlat, currency, viewabilityMethod, variant])

  const chunkSeries = useMemo(() => {
    if (!stats || !isFlat) return null
    return extractChunkSeries(stats, variant)
  }, [stats, isFlat, variant])

  // The fields the tiles plot from series of their own; the all-stats list
  // plots everything else the period reported.
  const tiledKeys = useMemo(
    () => metrics.map((m) => metricField(m.key, variant)),
    [metrics, variant]
  )

  // What the all-stats list can plot: the catalogued metrics the tiles leave
  // out that the period reported. The API builds chunks the way it builds the
  // totals, so a metric in the totals is one the chunks carry — a bucket it is
  // missing from, or a period with no chunks at all, simply delivered nothing.
  const listedSeries = useMemo(() => {
    const series = new Map<string, number[]>()
    if (!stats || !isFlat) return series
    const reported = (stats.totals ?? {}) as StatsTotals
    const read = chunkReader(stats)
    for (const [key, metric] of Object.entries(STAT_METRIC_CATALOG)) {
      if (tiledKeys.includes(key)) continue
      if (typeof reported[key] !== 'number' && !isCurrencyAmount(reported[key])) continue
      // Rates come as fractions; the chart reads them as percentages, like CTR.
      series.set(
        key,
        read(key).map((v) => (metric.format === 'ratio' ? v * 100 : v))
      )
    }
    return series
  }, [stats, isFlat, tiledKeys])

  const chartData = useMemo(() => {
    if (!stats || !chunkSeries) return null
    return buildChartData(stats, chunkSeries, listedSeries, effectiveRange, variant)
  }, [stats, chunkSeries, listedSeries, effectiveRange, variant])

  // The slot of a still-open last bucket, whose line is drawn dashed; -1 when
  // every bucket is closed.
  const openSlot = useMemo(
    () => (stats && chartData && isLastChunkOpen(stats, dataUpdatedAt) ? chartData.length - 1 : -1),
    [stats, chartData, dataUpdatedAt]
  )

  // Changes made to the object inside the charted period, bucketed onto the
  // same time axis as the delivery they may have moved.
  const changeEntries = useChangeMarkers({
    objectType: auditObject?.type ?? 'lineItem',
    objectId: auditObject?.id ?? '',
    startDate,
    endDate,
    enabled: !!auditObject,
  })

  const changeMarkers = useMemo(() => {
    if (!auditObject || !stats || !chartData) return []
    return groupChangesByChunk(
      changeEntries,
      (stats.chunkRange ?? []) as string[],
      chartData.map((row) => row.time)
    )
  }, [auditObject, stats, chartData, changeEntries])

  const markersByLabel = useMemo(
    () => new Map(changeMarkers.map((marker) => [marker.label, marker])),
    [changeMarkers]
  )

  const eCpmBase =
    eCpmMethod === 'PER_RENDERED' ? (totals?.rendered ?? 0) : (totals?.impressions ?? 0)
  const eCpm = totals && eCpmBase > 0 ? (totals.earnings / eCpmBase) * 1000 : null
  // The buyer's side of the same rate — what the impressions cost per thousand.
  const costECpm = totals && eCpmBase > 0 ? (totals.cost / eCpmBase) * 1000 : null

  const fillRate =
    totals && totals.requests > 0 ? (totals.impressions / totals.requests) * 100 : null

  const impressionsPerUser =
    totals && totals.uniqueUsers > 0 ? totals.impressions / totals.uniqueUsers : null

  const tileLine = ({ key, labelKey, color, scale }: MetricDef): ChartMetric => ({
    key: metricField(key, variant),
    labelKey,
    color,
    scale,
  })
  const chosenTiles = metrics.filter((m) => selectedMetrics.includes(metricField(m.key, variant)))
  // Lines from the all-stats list take the colours no tile line is using. A
  // saved choice from before the cap could name more than there are; the
  // extra lines are left off.
  const tileColors = new Set(chosenTiles.map((m) => m.color))
  const freeColors = CHART_COLORS.filter((color) => !tileColors.has(color))
  const chosenListed = selectedMetrics
    .filter((field) => listedSeries.has(field))
    .slice(0, freeColors.length)
    .map(
      (field, i): ChartMetric => ({
        key: field,
        labelKey: STAT_METRIC_CATALOG[field].labelKey,
        scale: SCALE_BY_FORMAT[STAT_METRIC_CATALOG[field].format],
        color: freeColors[i],
      })
    )

  // A saved choice can name only metrics this panel does not show; plot the
  // defaults rather than an empty chart — the ones still on the panel, if any are.
  const chosenMetrics = [...chosenTiles.map(tileLine), ...chosenListed]
  const defaultLines = metrics.filter((m) => DEFAULT_METRICS.includes(m.key))
  const shownDefaultLines = defaultLines.filter(
    (m) => layout.displayOf(metricField(m.key, variant)) !== 'hidden'
  )
  const activeMetrics =
    chosenMetrics.length > 0
      ? chosenMetrics
      : (shownDefaultLines.length > 0 ? shownDefaultLines : defaultLines).map(tileLine)
  const activeKeys = new Set(activeMetrics.map((m) => m.key))
  const chartColors = Object.fromEntries(activeMetrics.map((m) => [m.key, m.color]))

  const toggleMetric = (key: string) => {
    // Every line has a colour of its own, so the palette caps how many fit.
    if (!activeKeys.has(key) && activeMetrics.length >= CHART_COLORS.length) {
      toast.warning(t('stats.maxChartLines', { count: CHART_COLORS.length }))
      return
    }
    setSelectedMetrics((prev) => {
      // Never leave the chart empty — clicking the only selected metric keeps it.
      const next =
        prev.includes(key) && prev.length > 1
          ? prev.filter((k) => k !== key)
          : prev.includes(key)
            ? prev
            : [...prev, key]
      localStorage.setItem(metricsKey, JSON.stringify(next))
      return next
    })
  }
  /** Places a metric on the panel; taking it off the panel takes its line off the chart too. */
  const placeMetric = (key: string, display: StatDisplay) => {
    layout.setDisplay(key, display)
    if (display !== 'hidden') return
    setSelectedMetrics((prev) => {
      if (!prev.includes(key)) return prev
      const rest = prev.filter((k) => k !== key)
      // Stored as the defaults still on the panel rather than as nothing, which
      // a reload would read back as the defaults — the hidden one among them.
      const next =
        rest.length > 0
          ? rest
          : shownDefaultLines.map((m) => metricField(m.key, variant)).filter((k) => k !== key)
      localStorage.setItem(metricsKey, JSON.stringify(next))
      return next
    })
  }
  const axisOf = (metric: ChartMetric) => (axisMode === 'shared' ? SHARED_AXIS : metric.key)
  // A shared axis can only be read in a unit when every line is in that unit.
  const sharedScale = activeMetrics.every((m) => m.scale === activeMetrics[0].scale)
    ? activeMetrics[0].scale
    : undefined

  /** An axis tick — shorter than the value in the tooltip, and without currency. */
  const formatTick = (scale: MetricScale | undefined, value: number) =>
    scale === 'rate' ? formatPercent(value, 1) : formatFullNumber(value)

  const formatMetricValue = (metric: ChartMetric, value: number) => {
    if (metric.scale === 'currency') return formatCurrency(value, currency)
    if (metric.scale === 'rate') return formatPercent(value)
    if (metric.scale === 'decimal') {
      return formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    return formatFullNumber(value)
  }

  /** Tile values/subs, derived from the API totals. */
  const tileContent: Record<MetricKey, { value: string; sub?: string | null }> = totals
    ? {
        requests: {
          value: formatFullNumber(totals.requests),
          sub:
            fillRate != null
              ? t(variant === 'advertising' ? 'stats.winRate' : 'stats.fillRate', {
                  value: formatPercent(fillRate, 0),
                })
              : null,
        },
        impressions: {
          value: formatFullNumber(totals.impressions),
          sub: isMarketplaceBuyer
            ? fillRate != null
              ? t('stats.winRate', { value: formatPercent(fillRate, 0) })
              : null
            : statLabel('stats.renderedPct', { value: formatPercent(totals.renderedRate, 0) }),
        },
        rendered: {
          value: formatFullNumber(totals.rendered),
          sub: t('stats.renderRateValue', { value: formatPercent(totals.renderedRate, 0) }),
        },
        viewables: {
          value: formatFullNumber(totals.viewable),
          sub: `${formatPercent(totals.viewability, 0)} ${t('stats.viewability').toLowerCase()}`,
        },
        clicks: { value: formatFullNumber(totals.clicks) },
        ctr: {
          value: formatPercent(totals.ctr),
          sub: statLabel('stats.ctrSub', {
            clicks: formatFullNumber(totals.clicks),
            impressions: formatFullNumber(totals.impressions),
          }),
        },
        earnings: {
          value: formatCurrency(totals.earnings, currency),
          sub:
            eCpm != null ? t('stats.ecpmValue', { value: formatCurrency(eCpm, currency) }) : null,
        },
        cost: {
          value: formatCurrency(totals.cost, currency),
          sub:
            costECpm != null
              ? t('stats.ecpmValue', { value: formatCurrency(costECpm, currency) })
              : null,
        },
        totalCost: {
          value: formatCurrency(totals.totalCost, currency),
          sub: t('stats.totalCostSub'),
        },
        uniqueUsers: {
          value: formatFullNumber(totals.uniqueUsers),
          sub:
            impressionsPerUser != null
              ? `${formatNumber(impressionsPerUser, { maximumFractionDigits: 1 })} ${statLabel(
                  'stats.impressionsPerUser'
                ).toLowerCase()}`
              : null,
        },
      }
    : ({} as Record<MetricKey, { value: string; sub?: string | null }>)

  const cardHead = (
    <ListCardHeader
      title={
        <>
          <CardIcon icon={ChartLine} />
          {t('stats.statistics')}
        </>
      }
      actions={
        <>
          {totals && stats?.totals && (
            <AllStatsPopover
              totals={stats.totals}
              chartableKeys={[...tiledKeys, ...listedSeries.keys()]}
              chartColors={chartColors}
              onToggleChart={toggleMetric}
              placeableKeys={[...tiledKeys, ...listedSeries.keys()]}
              displayOf={layout.displayOf}
              onDisplayChange={placeMetric}
              onResetLayout={layout.isCustomized ? layout.reset : undefined}
            />
          )}
          {/* With one line the choice changes nothing, so it is only offered for more. */}
          {totals && activeMetrics.length > 1 && (
            <Select value={axisMode} onValueChange={(v) => changeAxisMode(v as AxisMode)}>
              <SelectTrigger size="sm" className="w-auto" aria-label={t('stats.yAxes')}>
                <SelectValue>{t(AXIS_MODE_LABELS[axisMode])}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(AXIS_MODE_LABELS) as AxisMode[]).map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {t(AXIS_MODE_LABELS[mode])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DateRangeSelect value={range} onChange={setRange} flight={flight} />
        </>
      }
    />
  )

  if (isLoading) {
    return (
      <Card surface={surface}>
        {cardHead}
        <CardContent className="block">
          <Skeleton className="h-45 w-full rounded-lg" />
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-17 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card surface={surface}>
        {cardHead}
        <CardContent className="block">
          <div className="flex items-center gap-2 text-destructive">
            <CircleAlert className="size-4" />
            <span className="text-sm">{(error as Error).message}</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!stats || !totals) {
    return (
      <Card surface={surface}>
        {cardHead}
        <CardContent className="block">
          <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
            <BarChart3 className="size-6" />
            <span className="text-sm">{t('stats.noData')}</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  // The panel's own metrics first, then whatever was placed from the all-stats
  // list, each read the same way whether it is a tile or a row.
  const reported = stats.totals as StatsTotals
  const placed: PlacedMetric[] = [
    ...metrics.map((metric) => ({
      key: metricField(metric.key, variant),
      label: statLabel(metric.labelKey),
      icon: metric.icon,
      color: metric.color,
      trend: chunkSeries?.[metric.key],
      ...tileContent[metric.key],
    })),
    ...[...listedSeries].map(([key, trend]) => {
      const metric = STAT_METRIC_CATALOG[key]
      return {
        key,
        label: statLabel(metric.labelKey),
        icon: metric.group ? GROUP_ICONS[metric.group] : BarChart3,
        // A colour of its own only once it has a line in the chart.
        color: chartColors[key] ?? 'var(--ad-text-muted)',
        trend,
        value: formatStatValue(reported[key], metric.format) ?? '–',
      }
    }),
  ]
  const tiles = placed.filter((metric) => layout.displayOf(metric.key) === 'card')
  const rows = placed.filter((metric) => layout.displayOf(metric.key) === 'table')

  return (
    <Card surface={surface}>
      {cardHead}
      <CardContent className="block">
        {/* Time series for the selected metrics */}
        <div className="rounded-lg border bg-(--ad-surface-raised) p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {dateRangeLabel(range, t)}
            </span>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {activeMetrics.map((metric) => (
                <span key={metric.key} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ background: metric.color }} />
                  {statLabel(metric.labelKey)}
                </span>
              ))}
              {changeMarkers.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-px bg-(--ad-text-faint)" />
                  {t('stats.changes')}
                </span>
              )}
            </div>
          </div>
          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart
                data={chartData}
                // Change flags are centred on the top of the plot area, so the
                // margin has to clear their radius or they are cut in half.
                margin={{
                  left: 0,
                  right: 4,
                  top: changeMarkers.length > 0 ? CHANGE_FLAG_RADIUS + 4 : 4,
                  bottom: 0,
                }}
              >
                <CartesianGrid
                  vertical={false}
                  yAxisId={axisOf(activeMetrics[0])}
                  stroke="var(--ad-border)"
                  strokeDasharray="3 3"
                />
                {changeMarkers.map((marker) => (
                  <ReferenceLine
                    key={marker.chunkStart}
                    yAxisId={axisOf(activeMetrics[0])}
                    x={marker.label}
                    stroke="var(--ad-text-faint)"
                    strokeDasharray="4 3"
                    label={<ChangeFlag count={marker.entries.length} />}
                  />
                ))}
                <XAxis
                  dataKey="time"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10, fill: 'var(--ad-text-muted)' }}
                  // Every nth label rather than "whatever fits", which leaves
                  // ragged holes next to the forced end label.
                  interval="equidistantPreserveStart"
                  minTickGap={8}
                />
                {axisMode === 'shared' ? (
                  <YAxis
                    yAxisId={SHARED_AXIS}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: 'var(--ad-text-muted)' }}
                    tickFormatter={(v: number) => formatTick(sharedScale, v)}
                    width="auto"
                  />
                ) : (
                  // Alternating sides, so the plot keeps its middle however
                  // many lines are on it.
                  activeMetrics.map((metric, i) => (
                    <YAxis
                      key={metric.key}
                      yAxisId={metric.key}
                      orientation={i % 2 === 0 ? 'left' : 'right'}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: metric.color }}
                      tickFormatter={(v: number) => formatTick(metric.scale, v)}
                      width="auto"
                    />
                  ))
                )}
                <Tooltip
                  // Lifted over the tiles below, which it overflows onto when
                  // many lines are plotted.
                  wrapperStyle={{ zIndex: 10 }}
                  cursor={{ stroke: 'var(--ad-border-strong)', strokeWidth: 1 }}
                  formatter={(value, _name, item) => {
                    const metric = activeMetrics.find((m) => m.key === item?.dataKey)
                    const num = Number(value) || 0
                    return metric ? formatMetricValue(metric, num) : formatFullNumber(num)
                  }}
                  contentStyle={{
                    fontSize: 11,
                    background: 'var(--ad-surface)',
                    border: '1px solid var(--ad-border)',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                  labelStyle={{ color: 'var(--ad-text-muted)' }}
                  labelFormatter={(label) => {
                    const marker = markersByLabel.get(String(label))
                    if (!marker) return label
                    // The messages themselves, so the chart answers "what
                    // changed here?" without a trip to the activity card.
                    const messages = marker.entries.flatMap((entry) => entry.messages)
                    return (
                      <span>
                        {label}
                        <span className="mt-1 block font-semibold text-(--ad-text)">
                          {t('stats.changeCount', { count: marker.entries.length })}
                        </span>
                        {messages.slice(0, 3).map((message, index) => (
                          <span key={index} className="block text-(--ad-text-muted)">
                            {auditMessageText(t, message)}
                          </span>
                        ))}
                        {messages.length > 3 && (
                          <span className="block text-(--ad-text-muted)">
                            {t('audit.moreChanges', { count: messages.length - 3 })}
                          </span>
                        )}
                      </span>
                    )
                  }}
                />
                {activeMetrics.flatMap((metric) => {
                  // Hidden through its opacity, not its colour: the tooltip
                  // writes each value in its line's stroke.
                  const line = (visible: boolean) => (
                    <Line
                      key={metric.key}
                      yAxisId={axisOf(metric)}
                      type="monotone"
                      dataKey={metric.key}
                      name={statLabel(metric.labelKey)}
                      stroke={metric.color}
                      strokeOpacity={visible ? 1 : 0}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3, fill: metric.color }}
                      isAnimationActive={false}
                    />
                  )
                  if (openSlot < 0) return [line(true)]
                  // An open last bucket is still filling up, so the stretch
                  // into it is dashed. The full line stays, unstroked, to
                  // carry the tooltip and hover dot across both pieces.
                  const piece = (part: 'closed' | 'open', inPart: (slot: number) => boolean) => (
                    <Line
                      key={`${metric.key}-${part}`}
                      yAxisId={axisOf(metric)}
                      type="monotone"
                      dataKey={(row: ChartRow) =>
                        inPart(Number(row.slot)) ? row[metric.key] : null
                      }
                      stroke={metric.color}
                      strokeWidth={2}
                      strokeDasharray={part === 'open' ? '4 3' : undefined}
                      dot={false}
                      activeDot={false}
                      tooltipType="none"
                      isAnimationActive={false}
                    />
                  )
                  return [
                    piece('closed', (slot) => slot < openSlot),
                    piece('open', (slot) => slot >= openSlot - 1),
                    line(false),
                  ]
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-45 items-center justify-center text-sm text-muted-foreground">
              {t('stats.noData')}
            </div>
          )}
        </div>

        {/* KPI tiles — click to plot the metric in the chart above */}
        {tiles.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {tiles.map((metric) => (
              <StatTile
                key={metric.key}
                icon={metric.icon}
                color={metric.color}
                label={metric.label}
                value={metric.value}
                sub={metric.sub}
                trend={metric.trend}
                selected={activeKeys.has(metric.key)}
                onClick={() => toggleMetric(metric.key)}
                action={
                  <UiTooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t('stats.showInTable')}
                        className="text-muted-foreground"
                        onClick={() => placeMetric(metric.key, 'table')}
                      >
                        <Rows3 />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('stats.showInTable')}</TooltipContent>
                  </UiTooltip>
                }
              />
            ))}
          </div>
        )}

        {/* The metrics the user would rather read as rows than tiles */}
        <StatTable
          rows={rows.map((metric) => ({ ...metric, selected: activeKeys.has(metric.key) }))}
          onToggleChart={toggleMetric}
          onShowAsCard={(key) => placeMetric(key, 'card')}
        />
      </CardContent>
    </Card>
  )
})
